/**
 * Input validation utilities for hermit-μDC configurator
 */

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

// IPv4 address pattern
const IPV4_REGEX = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

// CIDR notation (e.g., 192.168.1.0/24)
const CIDR_REGEX = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\/(?:3[0-2]|[12]?[0-9])$/;

// Hostname pattern (RFC 1123)
const HOSTNAME_REGEX = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/i;

// Domain pattern (e.g., hermit.local)
const DOMAIN_REGEX = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/i;

// MAC address pattern
const MAC_REGEX = /^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/;

// Port number (1-65535)
const PORT_REGEX = /^([1-9][0-9]{0,3}|[1-5][0-9]{4}|6[0-4][0-9]{3}|65[0-4][0-9]{2}|655[0-2][0-9]|6553[0-5])$/;

// WireGuard public key (base64, 44 chars)
const WG_PUBKEY_REGEX = /^[A-Za-z0-9+/]{43}=$/;

/**
 * Validate IPv4 address
 */
export function validateIPv4(value: string): ValidationResult {
  if (!value) {
    return { valid: false, error: 'IP address is required' };
  }
  if (!IPV4_REGEX.test(value)) {
    return { valid: false, error: 'Invalid IP address (e.g., 192.168.200.1)' };
  }
  return { valid: true };
}

/**
 * Validate CIDR notation subnet
 */
export function validateCIDR(value: string): ValidationResult {
  if (!value) {
    return { valid: false, error: 'Subnet is required' };
  }
  if (!CIDR_REGEX.test(value)) {
    return { valid: false, error: 'Invalid subnet (e.g., 192.168.200.0/24)' };
  }
  return { valid: true };
}

/**
 * Validate hostname (single label, no dots)
 */
export function validateHostname(value: string): ValidationResult {
  if (!value) {
    return { valid: false, error: 'Hostname is required' };
  }
  if (value.length > 63) {
    return { valid: false, error: 'Hostname must be 63 characters or less' };
  }
  if (!HOSTNAME_REGEX.test(value)) {
    return { valid: false, error: 'Invalid hostname (letters, numbers, hyphens only)' };
  }
  if (value.startsWith('-') || value.endsWith('-')) {
    return { valid: false, error: 'Hostname cannot start or end with hyphen' };
  }
  return { valid: true };
}

/**
 * Validate domain name
 */
export function validateDomain(value: string): ValidationResult {
  if (!value) {
    return { valid: false, error: 'Domain is required' };
  }
  if (value.length > 253) {
    return { valid: false, error: 'Domain must be 253 characters or less' };
  }
  if (!DOMAIN_REGEX.test(value)) {
    return { valid: false, error: 'Invalid domain (e.g., hermit.local)' };
  }
  return { valid: true };
}

/**
 * Validate MAC address
 */
export function validateMAC(value: string): ValidationResult {
  if (!value) {
    return { valid: true }; // MAC is optional
  }
  if (!MAC_REGEX.test(value)) {
    return { valid: false, error: 'Invalid MAC address (e.g., 00:11:22:33:44:55)' };
  }
  return { valid: true };
}

/**
 * Validate port number
 */
export function validatePort(value: string | number): ValidationResult {
  const portStr = String(value);
  if (!portStr) {
    return { valid: false, error: 'Port is required' };
  }
  if (!PORT_REGEX.test(portStr)) {
    return { valid: false, error: 'Port must be 1-65535' };
  }
  return { valid: true };
}

/**
 * Validate WireGuard public key
 */
export function validateWireGuardKey(value: string): ValidationResult {
  if (!value) {
    return { valid: false, error: 'Public key is required' };
  }
  if (!WG_PUBKEY_REGEX.test(value)) {
    return { valid: false, error: 'Invalid WireGuard public key (base64, 44 chars)' };
  }
  return { valid: true };
}

/**
 * Validate node name
 */
export function validateNodeName(value: string): ValidationResult {
  if (!value) {
    return { valid: false, error: 'Name is required' };
  }
  if (value.length < 2) {
    return { valid: false, error: 'Name must be at least 2 characters' };
  }
  if (value.length > 64) {
    return { valid: false, error: 'Name must be 64 characters or less' };
  }
  if (!/^[a-zA-Z0-9][a-zA-Z0-9\s-]*[a-zA-Z0-9]$/.test(value) && value.length > 1) {
    return { valid: false, error: 'Name must start/end with letter or number' };
  }
  return { valid: true };
}

/**
 * Check if IP is in subnet
 */
export function isIPInSubnet(ip: string, subnet: string): boolean {
  if (!validateIPv4(ip).valid || !validateCIDR(subnet).valid) {
    return false;
  }

  const [subnetIP, maskBits] = subnet.split('/');
  const mask = ~(2 ** (32 - parseInt(maskBits, 10)) - 1);

  const ipNum = ipToNumber(ip);
  const subnetNum = ipToNumber(subnetIP);

  return (ipNum & mask) === (subnetNum & mask);
}

/**
 * Convert IP string to number
 */
function ipToNumber(ip: string): number {
  return ip.split('.').reduce((acc, octet) => (acc << 8) + parseInt(octet, 10), 0) >>> 0;
}

/**
 * Validate IP is within subnet range
 */
export function validateIPInSubnet(ip: string, subnet: string): ValidationResult {
  const ipResult = validateIPv4(ip);
  if (!ipResult.valid) return ipResult;

  if (!isIPInSubnet(ip, subnet)) {
    return { valid: false, error: `IP must be within ${subnet}` };
  }
  return { valid: true };
}

/**
 * Check for duplicate IPs
 */
export function validateUniqueIP(ip: string, existingIPs: string[], currentIP?: string): ValidationResult {
  const ipResult = validateIPv4(ip);
  if (!ipResult.valid) return ipResult;

  const others = currentIP ? existingIPs.filter((i) => i !== currentIP) : existingIPs;
  if (others.includes(ip)) {
    return { valid: false, error: 'IP address already in use' };
  }
  return { valid: true };
}

/**
 * Check for duplicate hostnames
 */
export function validateUniqueHostname(hostname: string, existingHostnames: string[], currentHostname?: string): ValidationResult {
  const hostResult = validateHostname(hostname);
  if (!hostResult.valid) return hostResult;

  const others = currentHostname ? existingHostnames.filter((h) => h !== currentHostname) : existingHostnames;
  if (others.includes(hostname.toLowerCase())) {
    return { valid: false, error: 'Hostname already in use' };
  }
  return { valid: true };
}
